import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AtSign, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Two banners about the handle, mounted once in Layout (issue 500).
 *
 * THE NOTICE comes first and cannot be dismissed without acknowledging it: an
 * admin removed (or set) this person's handle, and a handle that silently
 * vanishes and re-prompts reads as a bug. The copy says what happened and
 * why, attributed to the admin team and to no individual (§1.2, decided).
 *
 * THE PROMPT is for an account with no handle — an existing account, or a
 * Google signup that never saw the register form — asked on next login and
 * free to dismiss for the session (§1: "prompted on next login, and can
 * dismiss it"). It is a banner and not a modal: nothing on the site needs a
 * handle except being invited by one, so nothing is blocked. A locked
 * account gets no prompt (§1.3). On the account page itself the card is on
 * screen already, so the banner stays out of the way there.
 */

const DISMISS_KEY = 'handlePromptDismissed';

function readDismissed() {
  try { return window.sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
}

export default function HandleBanner() {
  const { user, isAuthenticated, acknowledgeHandleNotice } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [acking, setAcking] = useState(false);

  if (!isAuthenticated || !user) return null;

  if (user.handle_notice) {
    const n = user.handle_notice;
    const assigned = n.kind === 'assigned';
    return (
      <div className="card mb-4" role="status" data-testid="handle-notice"
        style={{ borderColor: 'var(--accent, #C9A84C)' }}>
        <p className="font-medium text-primary">
          {assigned
            ? <>The admin team set your handle to <strong>@{n.handle}</strong>.</>
            : <>The admin team removed your handle <strong>@{n.handle}</strong>.</>}
        </p>
        {n.reason && <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Reason: {n.reason}</p>}
        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
          {assigned
            ? 'Only the admin team can change it now.'
            : 'You can choose a new one on your account page.'}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button type="button" className="btn btn-primary" disabled={acking}
            onClick={async () => { setAcking(true); try { await acknowledgeHandleNotice(); } finally { setAcking(false); } }}>
            Got it
          </button>
          {!assigned && <Link to="/account" className="btn btn-secondary">Choose a new handle</Link>}
        </div>
      </div>
    );
  }

  if (user.handle || user.handle_locked || dismissed || location.pathname === '/account') return null;

  return (
    <div className="card mb-4 flex flex-wrap items-center gap-3 print:hidden" role="note" aria-label="Choose a handle" data-testid="handle-prompt">
      <AtSign className="w-5 h-5 flex-shrink-0" style={{ color: '#C9A84C' }} aria-hidden="true" />
      <p className="flex-1 min-w-[12rem] text-sm" style={{ color: 'var(--text-secondary)' }}>
        Choose a handle so friends can invite you to their campaigns by name.
      </p>
      <Link to="/account" className="btn btn-primary">Choose a handle</Link>
      <button
        type="button"
        className="p-1"
        aria-label="Not now"
        title="Not now"
        onClick={() => {
          try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* private mode */ }
          setDismissed(true);
        }}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
